import "react-quill/dist/quill.snow.css";
import "./ComposeEmail.css";

import classNames from "classnames";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  HiOutlineDocumentText,
  HiOutlinePaperClip,
  HiOutlineTrash,
  HiXMark,
} from "react-icons/hi2";
import {
  LuCheck,
  LuChevronDown,
  LuRefreshCw,
  LuRotateCcw,
  LuSend,
} from "react-icons/lu";
import ReactQuill from "react-quill";
import { toast } from "react-toastify";
import { v4 } from "uuid";

import { SettingsConfirmModal } from "../../../components/modal/SettingsConfirmModal";
import { WarningExclaimSvg } from "../../../components/svg/WarningExclaimSvg";
import { Email } from "../../../factories/emails/models/emails.models";
import { Attachment } from "../../../factories/emails/models/send-email.model";
import { useAppSelector } from "../../../redux/redux";
import { Invoice } from "../../../services/BillingService";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  AiWriteAction,
  AiWriteRequest,
  useAiWriteMutation,
} from "../../../slices/AiSlice";
import { useLazyGetOrgContactQuery } from "../../../slices/ContactsSlice";
import {
  useLazyGetLinkedWorkQuery,
  useReplyEmailMutation,
  useSendEmailMutation,
} from "../../../slices/EmailsSlice";
import { useGetEmailTemplatesQuery } from "../../../slices/EmailTemplatesSlice";
import {
  aiErrorMessage,
  plainTextToQuillHtml,
  quillHtmlToPlainText,
} from "../../ai/ai.utils";
import { useAiStatus } from "../../ai/useAiStatus";
import {
  buildEmailTemplateContext,
  EmailTemplateRecipient,
  resolveEmailTemplate,
} from "../../settings/templates/emailTemplates/emailTemplates.utils";
import { ComposeAiPanel } from "./ComposeAiPanel";
import {
  attachmentsProblem,
  base64Bytes,
  buildDraftInstruction,
  composeChecks,
  ComposeTone,
  forwardHeaderHtml,
  forwardSubject,
  IMPROVE_ACTIONS,
  initialsOf,
  NO_MAILBOX_TEXT,
  NO_MAILBOX_TIP,
  nylasRecipients,
  quoteDateLine,
  replyRecipients,
  replySubject,
  revealSteps,
  SendFailure,
  sendFailure,
  sendSizeProblem,
} from "./composeEmail.utils";
import { RecipientField } from "./RecipientField";
import { SparkleIcon } from "./SparkleIcon";

export type ComposeAiContext = {
  /** One or two sentences about the email being answered (from the Mail page AI). */
  summary?: string;
  /** Ready-made replies (Mail page "Quick replies"); picking one needs no AI call. */
  replies?: { label: string; text: string }[];
};

export type ComposeEmailProps = {
  isActive: boolean;
  onClose: () => void;
  isReply?: boolean;
  isReplyAll?: boolean;
  isForward?: boolean;
  email?: Email;
  contactEmail?: EmailTemplateRecipient[];
  customersEmail?: EmailTemplateRecipient[];
  selectedMail?: Email | null;
  bulk?: boolean;
  invoiceEmail?: string | null;
  invoiceFile?: Attachment;
  invoice?: Invoice;
  /** Plain text to start with (e.g. a quick reply); shown with the AI review bar. */
  initialText?: string;
  initialTextLabel?: string;
  /** Open with the AI assistant's prompt filled in and generate straight away. */
  initialPrompt?: string;
  aiContext?: ComposeAiContext;
  /** Work item / client for the AI's context (names and due date only). */
  workId?: string;
  customerId?: string;
};

const fileToBase64 = (file: File): Promise<string | undefined> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result?.toString().split(",")[1]);
    reader.onerror = (error) => {
      reader.abort();
      reject(error);
    };
    reader.readAsDataURL(file);
  });

type Snapshot = { html: string; subject: string };
type LastAi =
  | { kind: "improve"; action: AiWriteAction; source: string }
  | { kind: "draft"; prompt: string; source: string };

/**
 * The compose window (V53), used everywhere an email is written: Mail page,
 * client Email tabs and profiles, Contacts list, work Emails tab, invoices.
 * Left: recipients, subject and editor. Right (when the firm has AI on): the
 * AI assistant that drafts from a description or improves what's written.
 * AI text is typed into the editor and can be kept, retried or undone.
 */
export const ComposeEmailWindow: FC<ComposeEmailProps> = ({
  isActive,
  onClose,
  isReply,
  isReplyAll,
  isForward,
  email,
  contactEmail,
  customersEmail,
  selectedMail,
  bulk,
  invoice,
  invoiceEmail,
  invoiceFile,
  initialText,
  initialTextLabel,
  initialPrompt,
  aiContext,
  workId: workIdProp,
  customerId: customerIdProp,
}) => {
  const { user } = useAppSelector((state) => state.appReducer);
  const myEmail = (user?.nylasToken?.email || "").toLowerCase();
  const orgId = getLinkedFirm()?.orgId || "";
  const { isOn: aiOn } = useAiStatus();
  const isAnswer = !!(isReply || isReplyAll) && !!email;
  const isFwd = !!isForward && !!email;

  // ----- Recipients and fields -----
  // Reply / Reply all (QA batch 19, MAIL-023): an email I sent is answered to
  // the people it went to, and my own address is never added.
  const [answer] = useState(() =>
    isAnswer && email
      ? replyRecipients<EmailTemplateRecipient>(
          email as {
            from?: EmailTemplateRecipient[];
            to?: EmailTemplateRecipient[];
            cc?: EmailTemplateRecipient[];
            reply_to?: EmailTemplateRecipient[];
          },
          !!isReplyAll,
          [myEmail, user?.userProfile?.email],
        )
      : null,
  );
  const [toList, setToList] = useState<EmailTemplateRecipient[]>(() => {
    if (isFwd) return contactEmail || [];
    if (answer) return answer.to;
    return email?.from || contactEmail || [];
  });
  const [ccList, setCcList] = useState<EmailTemplateRecipient[]>(
    answer ? answer.cc : [],
  );
  const [bccList, setBccList] = useState<EmailTemplateRecipient[]>(
    isAnswer || !email
      ? ((email?.bcc || customersEmail || []) as EmailTemplateRecipient[])
      : [],
  );
  const [showCc, setShowCc] = useState(
    !!(ccList.length || bccList.length || bulk),
  );
  const [subject, setSubject] = useState(
    isFwd
      ? forwardSubject(email?.subject)
      : isAnswer
      ? replySubject(email?.subject)
      : email?.subject || "",
  );
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<(Attachment & { id: string })[]>([]);
  const [fileTooBig, setFileTooBig] = useState<string | null>(null);
  const [sendError, setSendError] = useState<SendFailure | null>(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // ----- Templates -----
  const [tplOpen, setTplOpen] = useState(false);
  const [tplId, setTplId] = useState<string | null>(null);
  const { data: emailTemplates = [] } = useGetEmailTemplatesQuery(
    { orgId },
    { skip: !orgId },
  );
  const activeTemplates = useMemo(
    () => emailTemplates.filter((t) => t.isActive),
    [emailTemplates],
  );
  const [getOrgContact] = useLazyGetOrgContactQuery();

  // ----- Sending -----
  const [getLinkedWork, { data: linkedWorkId }] = useLazyGetLinkedWorkQuery();
  const [sendEmail, { isSuccess: sent, isLoading: sending }] =
    useSendEmailMutation();
  const [replyEmail, { isSuccess: replied, isLoading: replying }] =
    useReplyEmailMutation();
  const [checkOpen, setCheckOpen] = useState(false);

  useEffect(() => {
    const thread = (selectedMail || email)?.thread_id;
    if (thread) getLinkedWork({ threadId: thread });
  }, [selectedMail?.thread_id, email?.thread_id]);

  useEffect(() => {
    if (sent || replied) {
      toast.success("Email sent");
      onClose();
    }
  }, [sent, replied]);

  useEffect(() => {
    if (invoiceEmail) {
      setToList((prev) =>
        prev.some((p) => p.email === invoiceEmail)
          ? prev
          : [
              ...prev,
              {
                email: invoiceEmail,
                customerName: invoice?.customerName,
                businessContactId: invoice?.businessContactId,
              },
            ],
      );
    }
  }, [invoiceEmail]);

  useEffect(() => {
    if (invoiceFile) setFiles([{ ...invoiceFile, id: invoice?.id || v4() }]);
  }, [invoiceFile]);

  // ----- AI -----
  const [aiWrite] = useAiWriteMutation();
  const [aiOpen, setAiOpen] = useState(true);
  const [improveOpen, setImproveOpen] = useState(false);
  const [prompt, setPrompt] = useState(initialPrompt || "");
  const [tone, setTone] = useState<ComposeTone>("professional");
  const [busy, setBusy] = useState<null | {
    label: string;
    field: "body" | "subject";
  }>(null);
  const [review, setReview] = useState<string | null>(null);
  const [prev, setPrev] = useState<Snapshot | null>(null);
  const [last, setLast] = useState<LastAi | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const requestId = useRef(0);
  const panelVisible = aiOn && aiOpen;

  useEffect(() => () => stopTimer(), []);

  const stopTimer = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  };

  const workId = workIdProp || linkedWorkId || undefined;
  const customerId =
    customerIdProp || toList.find((r) => r.customerId)?.customerId || undefined;
  const replyTo =
    (isAnswer || isFwd) && email
      ? {
          id: email.id,
          from: email.from?.[0]?.name || email.from?.[0]?.email,
          fromEmail: email.from?.[0]?.email,
          subject: email.subject,
          body: email.body,
          date: quoteDateLine(email).replace(/^On /, ""),
        }
      : undefined;

  /** Types the AI's text into the editor, then shows the review bar. */
  const reveal = (text: string, label: string, snapshot: Snapshot) => {
    stopTimer();
    setPrev(snapshot);
    setReview(null);
    setBusy({ label, field: "body" });
    const steps = revealSteps(text);
    let i = 0;
    timer.current = setInterval(() => {
      setMessage(plainTextToQuillHtml(text.slice(0, steps[i])));
      i++;
      if (i >= steps.length) {
        stopTimer();
        setBusy(null);
        setReview(label);
      }
    }, 22);
  };

  const callAi = async (
    request: Omit<AiWriteRequest, "orgId" | "purpose">,
    busyLabel: string,
    doneLabel: string,
    snapshot: Snapshot,
  ) => {
    const id = ++requestId.current;
    setBusy({ label: busyLabel, field: "body" });
    setReview(null);
    setCheckOpen(false);
    setImproveOpen(false);
    try {
      const res = await aiWrite({
        orgId,
        purpose: "email",
        workId,
        customerId,
        replyTo,
        ...request,
      }).unwrap();
      if (id !== requestId.current) return;
      if (res.subject && !subject.trim()) setSubject(res.subject);
      if (
        quillHtmlToPlainText(plainTextToQuillHtml(res.text)) ===
        quillHtmlToPlainText(snapshot.html)
      ) {
        setBusy(null);
        toast.info("Looks good — no changes needed");
        return;
      }
      reveal(res.text, doneLabel, snapshot);
    } catch (error) {
      if (id !== requestId.current) return;
      setBusy(null);
      toast.error(aiErrorMessage(error));
    }
  };

  const runImprove = (action: AiWriteAction, source?: string) => {
    const html = source ?? message;
    if (busy || !quillHtmlToPlainText(html)) return;
    const meta = IMPROVE_ACTIONS.find((a) => a.action === action);
    setLast({ kind: "improve", action, source: html });
    callAi(
      { action, text: html, subject },
      meta?.busy || "Writing…",
      meta?.done || "Changed by AI",
      {
        html,
        subject,
      },
    );
  };

  const runDraft = (text?: string, source?: string) => {
    const p = (text ?? prompt).trim();
    if (busy || !p) return;
    const html = source ?? message;
    setLast({ kind: "draft", prompt: p, source: html });
    callAi(
      {
        action: "draft",
        instruction: buildDraftInstruction(p, tone),
        subject: subject.trim() || undefined,
      },
      isAnswer ? "Drafting your reply…" : "Drafting your email…",
      "Drafted with AI",
      { html, subject },
    );
  };

  const applyReadyReply = (text: string, label: string) => {
    if (busy) return;
    setLast(null);
    reveal(text, label, { html: message, subject });
  };

  const suggestSubject = async () => {
    if (busy || !quillHtmlToPlainText(message)) {
      if (!quillHtmlToPlainText(message))
        toast.info(
          "Write your message first, then the AI can suggest a subject.",
        );
      return;
    }
    setBusy({ label: "Writing a subject…", field: "subject" });
    setCheckOpen(false);
    try {
      const res = await aiWrite({
        orgId,
        purpose: "email",
        action: "subject",
        text: message,
        workId,
        customerId,
      }).unwrap();
      if (res.subject) setSubject(res.subject);
    } catch (error) {
      toast.error(aiErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const tryAgain = () => {
    if (!last || busy) return;
    if (last.kind === "improve") runImprove(last.action, last.source);
    else runDraft(last.prompt, last.source);
  };

  const undo = () => {
    if (prev) {
      setMessage(prev.html);
      setSubject(prev.subject);
    }
    setReview(null);
    setPrev(null);
  };

  const stop = () => {
    requestId.current++;
    stopTimer();
    setBusy(null);
    if (prev) setReview("Stopped early");
  };

  // Start straight away with a ready reply or a prompt (Mail page buttons).
  useEffect(() => {
    if (initialText)
      applyReadyReply(initialText, initialTextLabel || "Drafted with AI");
    else if (initialPrompt && aiOn) runDraft(initialPrompt);
  }, [aiOn]);

  // ----- Templates -----
  const pickTemplate = async (templateId: string) => {
    setTplOpen(false);
    const template = emailTemplates.find((t) => t.id === templateId);
    if (!template) return;
    const recipient = toList[0] || bccList[0] || ccList[0];
    let context = buildEmailTemplateContext(recipient, invoice?.customerName);
    if (
      recipient?.businessContactId &&
      orgId &&
      (!recipient.contactPersonFirstName || !recipient.contactPersonLastName)
    ) {
      try {
        const organization = await getOrgContact({
          id: recipient.businessContactId,
          orgId,
        }).unwrap();
        context = buildEmailTemplateContext({
          ...recipient,
          customerName: recipient.customerName || organization.companyName,
          companyName: organization.companyName,
          contactPersonFirstName: organization.contactPersons?.firstName,
          contactPersonLastName: organization.contactPersons?.lastName,
        });
      } catch {
        // Keep the basic context.
      }
    }
    setTplId(templateId);
    setSubject(resolveEmailTemplate(template.subject, context));
    setMessage(resolveEmailTemplate(template.body, context));
    setReview(null);
  };

  // ----- Files -----
  const addFiles = async (list: FileList | null) => {
    const chosen = Array.from(list || []);
    const problem = attachmentsProblem(
      files.reduce((sum, f) => sum + base64Bytes(f.content), 0),
      chosen.map((f) => f.size),
    );
    if (problem) {
      setFileTooBig(problem);
      return;
    }
    setSendError(null);
    for (const file of chosen) {
      const content = await fileToBase64(file);
      if (content)
        setFiles((p) => [
          ...p,
          { filename: file.name, content_type: file.type, content, id: v4() },
        ]);
    }
    setCheckOpen(false);
  };

  // ----- Send -----
  const checks = busy
    ? []
    : composeChecks({ html: message, subject, attachmentCount: files.length });
  const bodyEmpty = !quillHtmlToPlainText(message);
  const hasRecipients = !!(toList.length || ccList.length || bccList.length);
  // No connected mailbox = nothing can be sent (QA batch 13: Send was enabled).
  const hasMailbox = !!user?.nylasToken?.email;
  const canSend = hasMailbox && hasRecipients && !busy && !sending && !replying;

  const quotedHtml = isAnswer
    ? `<br/>${quoteDateLine(
        email,
      )}<br/><div style="margin:0 0 0 0.8ex;border-left:1px solid rgb(204,204,204);padding-left:1ex">${
        email?.body || ""
      }</div>`
    : isFwd && email
    ? `<br/>${forwardHeaderHtml(email)}${email.body || ""}`
    : "";

  const send = async (force: boolean) => {
    if (!canSend) return;
    if (!force && checks.length) {
      setCheckOpen(true);
      return;
    }
    const attachments: Attachment[] = files.map(
      ({ content, content_type, filename }) => ({
        content,
        content_type,
        filename,
      }),
    );
    const to = nylasRecipients(toList);
    const cc = nylasRecipients(ccList);
    const bcc = nylasRecipients(bccList);
    const body = `${message}${quotedHtml}`;
    const sizeProblem = sendSizeProblem(
      new Blob([JSON.stringify({ subject, body, to, cc, bcc, attachments })])
        .size,
    );
    if (sizeProblem) {
      setCheckOpen(false);
      setSendError({ kind: "other", text: sizeProblem });
      return;
    }
    setSendError(null);
    // Roadmap 1873: a failed send used to do nothing at all (no message, the
    // window just stayed open). Now the reason is shown in the window and the
    // draft is kept so the person can fix it and click Send again.
    try {
      if (isAnswer && email) {
        await replyEmail({
          userId: user.id,
          body: {
            subject,
            reply_to: to,
            ...(linkedWorkId
              ? { reply_to_message_id: email.id || "" }
              : { thread_id: email.thread_id }),
            from: email.from,
            body,
            to,
            cc,
            bcc,
            attachments,
          },
        }).unwrap();
      } else {
        await sendEmail({
          userId: user.id,
          body: { subject, body, to, cc, bcc, attachments },
        }).unwrap();
      }
    } catch (error) {
      const failure = sendFailure(error);
      setCheckOpen(false);
      setSendError(failure);
      // The mailbox case already gets the "reconnect" toast from EmailsSlice.
      if (failure.kind !== "mailbox")
        toast.error(failure.text, { toastId: "compose-send-failed" });
    }
  };

  const title = isFwd
    ? "Forward"
    : isReplyAll
    ? "Reply all"
    : isReply
    ? "Reply"
    : "New email";
  const contextLine =
    (isAnswer || isFwd) && email
      ? `${isFwd ? "Forwarding" : "Replying to"} ${
          (!isFwd && answer?.to[0]
            ? answer.to[0].name || answer.to[0].email
            : email.from?.[0]?.name || email.from?.[0]?.email) || ""
        } · ${email.subject || ""}`
      : "";
  const tplName = activeTemplates.find((t) => t.id === tplId)?.name;
  const editorActive = busy?.field === "body" || !!review;
  const checkLabel = bodyEmpty
    ? "Check"
    : checks.length
    ? `${checks.length} ${checks.length === 1 ? "thing" : "things"} to check`
    : "Ready to send";

  if (!isActive) return null;

  return (
    <div
      className={"compose-email modal modal-open"}
      style={{ background: "rgba(20,22,34,0.5)" }}>
      <div
        role={"dialog"}
        aria-label={title}
        className={
          "flex max-h-[95vh] max-w-[96vw] flex-col overflow-hidden rounded-[16px] bg-white shadow-[0_24px_70px_rgba(0,0,0,0.3)]"
        }
        style={{ width: panelVisible ? 1140 : 860, height: 780 }}>
        {/* header */}
        <div
          className={
            "flex flex-none items-center gap-3 border-b border-[#ECEFF3] py-[14px] pl-[22px] pr-[18px]"
          }>
          <span className={"flex min-w-0 flex-1 flex-col"}>
            <span className={"text-[17px] font-extrabold text-[#1F2433]"}>
              {title}
            </span>
            {contextLine && (
              <span className={"truncate text-[12.5px] text-[#8A90A2]"}>
                {contextLine}
              </span>
            )}
          </span>
          {aiOn && (
            <button
              type={"button"}
              onClick={() => setAiOpen((v) => !v)}
              className={
                "inline-flex h-[34px] items-center gap-[7px] rounded-[9px] border-[1.5px] px-[13px] text-[13px] font-bold"
              }
              style={
                aiOpen
                  ? {
                      background: "#F1EEFF",
                      borderColor: "#CFC3FF",
                      color: "#5B3FE0",
                    }
                  : {
                      background: "#fff",
                      borderColor: "#DCE2EA",
                      color: "#3A4256",
                    }
              }>
              <SparkleIcon size={14} />
              AI assistant
            </button>
          )}
          <button
            type={"button"}
            aria-label={"Close"}
            onClick={onClose}
            className={
              "inline-flex h-8 w-8 items-center justify-center rounded-[8px] text-[#9097A8] hover:bg-[#F4F5F7] hover:text-[#3A4256]"
            }>
            <HiXMark size={18} />
          </button>
        </div>

        <div className={"flex min-h-0 flex-1"}>
          {/* LEFT: editor */}
          <div className={"flex min-h-0 min-w-0 flex-1 flex-col"}>
            <div
              className={
                "min-h-0 flex-1 overflow-y-auto px-[22px] pb-[18px] pt-[6px]"
              }>
              <div
                className={
                  "flex items-center gap-3 border-b border-[#EEF0F3] py-[7px]"
                }>
                <span
                  className={"w-[58px] flex-none text-[13px] text-[#8A90A2]"}>
                  From
                </span>
                <span
                  className={"flex h-[34px] min-w-0 flex-1 items-center gap-2"}>
                  <span
                    className={
                      "inline-flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#16A34A] text-[9px] font-bold text-white"
                    }>
                    {initialsOf(
                      `${user?.userProfile?.firstName || ""} ${
                        user?.userProfile?.lastName || ""
                      }`.trim() ||
                        user?.nylasToken?.email ||
                        "",
                    )}
                  </span>
                  <span
                    className={
                      "truncate text-[14px] font-semibold text-[#1F2433]"
                    }>
                    {user?.nylasToken?.email || (
                      <span
                        className={"font-semibold text-[#B42318]"}
                        title={NO_MAILBOX_TIP}>
                        {NO_MAILBOX_TEXT}
                      </span>
                    )}
                  </span>
                </span>
              </div>
              <RecipientField
                label={"To"}
                value={toList}
                onChange={setToList}
                placeholder={"Name, email or client"}
                autoFocus={!toList.length}
                extra={
                  !showCc && (
                    <button
                      type={"button"}
                      onClick={() => setShowCc(true)}
                      className={
                        "flex-none px-1 text-[13px] font-bold leading-[34px] text-[#6B4EFF]"
                      }>
                      Cc / Bcc
                    </button>
                  )
                }
              />
              {showCc && (
                <>
                  <RecipientField
                    label={"Cc"}
                    value={ccList}
                    onChange={setCcList}
                  />
                  <RecipientField
                    label={"Bcc"}
                    value={bccList}
                    onChange={setBccList}
                  />
                </>
              )}
              <div
                className={
                  "flex items-center gap-3 border-b border-[#EEF0F3] py-[6px]"
                }>
                <span
                  className={"w-[58px] flex-none text-[13px] text-[#8A90A2]"}>
                  Subject
                </span>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={"Subject"}
                  className={
                    "h-[34px] min-w-0 flex-1 rounded-[6px] border-none px-[2px] text-[14px] font-bold text-[#1F2433] outline-none transition-colors placeholder:font-medium placeholder:text-[#A3A9B8]"
                  }
                  style={{
                    background:
                      busy?.field === "subject" ? "#F1EEFF" : "transparent",
                  }}
                />
                {aiOn && (
                  <button
                    type={"button"}
                    title={"Write a subject line from your message"}
                    onClick={suggestSubject}
                    className={
                      "inline-flex h-7 flex-none items-center gap-[5px] rounded-[7px] px-[9px] text-[12.5px] font-bold text-[#6B4EFF] hover:bg-[#F4F2FF]"
                    }>
                    <SparkleIcon
                      size={13}
                      className={
                        busy?.field === "subject" ? "animate-pulse" : ""
                      }
                    />
                    Suggest
                  </button>
                )}
              </div>

              {/* editor card */}
              <div
                className={
                  "relative mt-4 rounded-[12px] border-[1.5px] bg-white transition-all"
                }
                style={{
                  borderColor: editorActive ? "#B9A9FF" : "#DCE2EA",
                  boxShadow: editorActive
                    ? "0 0 0 4px rgba(107,78,255,0.10)"
                    : "none",
                }}>
                <div
                  className={
                    "flex flex-wrap items-center gap-1 rounded-t-[11px] border-b border-[#ECEFF3] bg-[#FBFBFC] px-2 py-[6px]"
                  }>
                  <span
                    className={"pl-1 text-[12px] font-semibold text-[#9097A8]"}>
                    Message
                  </span>
                  <span className={"flex-1"} />
                  <span className={"relative"}>
                    <button
                      type={"button"}
                      onClick={() => {
                        setTplOpen((v) => !v);
                        setImproveOpen(false);
                      }}
                      className={
                        "inline-flex h-7 max-w-[220px] items-center gap-[6px] rounded-[6px] px-[9px] text-[12.5px] font-bold hover:bg-[#EEF0F3]"
                      }
                      style={{ color: tplName ? "#1F2433" : "#5B6678" }}>
                      <HiOutlineDocumentText
                        size={14}
                        className={"flex-none"}
                      />
                      <span className={"truncate"}>
                        {tplName || "Templates"}
                      </span>
                      <LuChevronDown size={12} className={"flex-none"} />
                    </button>
                    {tplOpen && (
                      <>
                        <span
                          className={"fixed inset-0 z-30"}
                          onClick={() => setTplOpen(false)}
                        />
                        <span
                          className={
                            "absolute right-0 top-[calc(100%+6px)] z-40 flex max-h-[260px] w-[260px] flex-col overflow-y-auto rounded-[11px] border border-[#E8E8EC] bg-white p-[6px] shadow-[0_14px_36px_rgba(20,22,34,0.18)]"
                          }>
                          <span
                            className={
                              "px-[10px] pb-1 pt-[6px] text-[11px] font-extrabold uppercase tracking-[0.4px] text-[#9097A8]"
                            }>
                            Email templates
                          </span>
                          {activeTemplates.length === 0 && (
                            <span
                              className={
                                "px-[10px] py-2 text-[13px] text-[#8A90A2]"
                              }>
                              No templates yet. Add them in Settings &gt;
                              Templates.
                            </span>
                          )}
                          {activeTemplates.map((t) => (
                            <button
                              type={"button"}
                              key={t.id}
                              onClick={() => pickTemplate(t.id)}
                              className={
                                "flex items-center justify-between gap-[10px] rounded-[8px] px-[10px] py-[9px] text-left text-[13.5px] font-semibold hover:bg-[#F4F2FF] hover:text-[#6B4EFF]"
                              }
                              style={{
                                color: t.id === tplId ? "#6B4EFF" : "#1F2433",
                                background:
                                  t.id === tplId ? "#F4F2FF" : undefined,
                              }}>
                              <span>{t.name}</span>
                              {t.id === tplId && (
                                <LuCheck
                                  size={15}
                                  className={"flex-none text-[#6B4EFF]"}
                                />
                              )}
                            </button>
                          ))}
                        </span>
                      </>
                    )}
                  </span>
                  {aiOn && (
                    <span className={"relative"}>
                      <button
                        type={"button"}
                        onClick={() => {
                          setImproveOpen((v) => !v);
                          setTplOpen(false);
                        }}
                        className={
                          "inline-flex h-7 items-center gap-[6px] rounded-[7px] bg-[#F1EEFF] px-[10px] text-[12.5px] font-bold text-[#5B3FE0] hover:bg-[#E7E1FF]"
                        }>
                        <SparkleIcon size={13} />
                        Improve
                        <LuChevronDown size={12} />
                      </button>
                      {improveOpen && (
                        <>
                          <span
                            className={"fixed inset-0 z-30"}
                            onClick={() => setImproveOpen(false)}
                          />
                          <span
                            className={
                              "absolute right-0 top-[calc(100%+6px)] z-40 flex w-[240px] flex-col rounded-[11px] border border-[#E8E8EC] bg-white p-[6px] shadow-[0_14px_36px_rgba(20,22,34,0.18)]"
                            }>
                            {bodyEmpty && (
                              <span
                                className={
                                  "px-[10px] py-[6px] text-[12px] text-[#A3A9B8]"
                                }>
                                Write something first
                              </span>
                            )}
                            {IMPROVE_ACTIONS.map((a) => (
                              <button
                                type={"button"}
                                key={a.action}
                                disabled={bodyEmpty || !!busy}
                                onClick={() => runImprove(a.action)}
                                className={
                                  "rounded-[8px] px-[10px] py-2 text-left text-[13.5px] font-semibold text-[#2A1F66] hover:bg-[#F4F2FF] disabled:cursor-not-allowed disabled:text-[#A3A9B8] disabled:hover:bg-transparent"
                                }>
                                {a.label}
                              </button>
                            ))}
                          </span>
                        </>
                      )}
                    </span>
                  )}
                </div>

                {busy?.field === "body" && (
                  <div
                    className={
                      "compose-ai-shimmer flex items-center gap-[9px] border-b border-[#E4DCFF] px-[14px] py-2"
                    }>
                    <SparkleIcon
                      size={14}
                      className={"compose-ai-spin text-[#6B4EFF]"}
                    />
                    <span className={"text-[13px] font-bold text-[#3A2E7A]"}>
                      {busy.label}
                    </span>
                    <span className={"flex-1"} />
                    <button
                      type={"button"}
                      onClick={stop}
                      className={
                        "inline-flex h-[26px] items-center gap-[5px] rounded-[7px] border border-[#DCD3FF] bg-white px-[10px] text-[12px] font-bold text-[#5B3FE0]"
                      }>
                      <span
                        className={
                          "inline-block h-[9px] w-[9px] rounded-[2px] bg-current"
                        }
                      />
                      Stop
                    </button>
                  </div>
                )}
                {review && !busy && (
                  <div
                    className={
                      "flex items-center gap-[10px] border-b border-[#E4DCFF] py-2 pl-[14px] pr-[10px]"
                    }
                    style={{
                      background: "linear-gradient(90deg,#F3EFFF,#FAF8FF)",
                    }}>
                    <SparkleIcon size={14} className={"text-[#6B4EFF]"} />
                    <span
                      className={
                        "min-w-0 flex-1 text-[13px] font-bold text-[#3A2E7A]"
                      }>
                      {review}
                      <span className={"font-medium text-[#8A80C0]"}>
                        {" "}
                        · Review before sending
                      </span>
                    </span>
                    {prev && (
                      <button
                        type={"button"}
                        onClick={undo}
                        className={
                          "inline-flex h-7 items-center gap-[5px] rounded-[7px] px-[10px] text-[12.5px] font-bold text-[#5B6678] hover:bg-[#EEE9FF] hover:text-[#3A2E7A]"
                        }>
                        <LuRotateCcw size={13} />
                        Undo
                      </button>
                    )}
                    {last && (
                      <button
                        type={"button"}
                        onClick={tryAgain}
                        className={
                          "inline-flex h-7 items-center gap-[5px] rounded-[7px] px-[10px] text-[12.5px] font-bold text-[#5B6678] hover:bg-[#EEE9FF] hover:text-[#3A2E7A]"
                        }>
                        <LuRefreshCw size={13} />
                        Try again
                      </button>
                    )}
                    <button
                      type={"button"}
                      onClick={() => {
                        setReview(null);
                        setPrev(null);
                      }}
                      className={
                        "inline-flex h-7 items-center gap-[5px] rounded-[7px] bg-[#6B4EFF] px-3 text-[12.5px] font-bold text-white hover:bg-[#5D4DB4]"
                      }>
                      <LuCheck size={13} />
                      Keep
                    </button>
                  </div>
                )}

                <div className={"relative"}>
                  <ReactQuill
                    theme={"snow"}
                    value={message}
                    readOnly={busy?.field === "body"}
                    placeholder={
                      panelVisible
                        ? "Write your message, or describe it to the AI assistant →"
                        : "Write your message"
                    }
                    onChange={(value, _delta, source) => {
                      setMessage(value);
                      if (source === "user" && review) setReview(null);
                    }}
                    className={"compose-quill"}
                  />
                  {aiOn &&
                    !panelVisible &&
                    !busy &&
                    !review &&
                    quillHtmlToPlainText(message).length > 15 && (
                      <button
                        type={"button"}
                        onClick={() => runImprove("improve")}
                        className={
                          "absolute bottom-3 right-3 inline-flex h-[30px] items-center gap-[6px] rounded-[15px] border border-[#DCD3FF] bg-white px-3 text-[12.5px] font-bold text-[#5B3FE0] shadow-[0_4px_14px_rgba(107,78,255,0.16)] hover:border-[#6B4EFF] hover:bg-[#6B4EFF] hover:text-white"
                        }>
                        <SparkleIcon size={13} />
                        Polish this email
                      </button>
                    )}
                </div>
              </div>

              {(isAnswer || isFwd) && email && (
                <div className={"mt-[10px]"}>
                  <button
                    type={"button"}
                    onClick={() => setQuoteOpen((v) => !v)}
                    className={
                      "inline-flex h-[26px] items-center rounded-[13px] bg-[#F3F4F7] px-[10px] text-[12px] font-bold text-[#5B6678] hover:bg-[#E9EBF0]"
                    }>
                    {quoteOpen
                      ? "Hide quoted text"
                      : isFwd
                      ? "•••  Show forwarded message"
                      : "•••  Show quoted text"}
                  </button>
                  {quoteOpen && (
                    <div
                      className={
                        "mt-2 border-l-[3px] border-[#E2E5EC] px-[14px] py-[10px] text-[13px] leading-[1.6] text-[#7D8396]"
                      }>
                      <div className={"mb-1 font-semibold"}>
                        {quoteDateLine(email)}
                      </div>
                      <div
                        className={"compose-quote"}
                        dangerouslySetInnerHTML={{ __html: email.body || "" }}
                      />
                    </div>
                  )}
                  {isFwd && !!email.attachments?.length && (
                    <div className={"mt-2 text-[12px] text-[#8A90A2]"}>
                      {
                        "The original email's attachments aren't included. Attach them again if needed."
                      }
                    </div>
                  )}
                </div>
              )}

              {files.length > 0 && (
                <div className={"mt-[14px] flex flex-wrap gap-[9px]"}>
                  {files.map((f) => (
                    <span
                      key={f.id}
                      className={
                        "inline-flex items-center gap-2 rounded-[9px] border border-[#E2E5EC] bg-white py-[7px] pl-3 pr-[9px] text-[13px] font-semibold text-[#3A4256]"
                      }>
                      <HiOutlineDocumentText
                        size={15}
                        className={"text-[#6B4EFF]"}
                      />
                      <span className={"max-w-[220px] truncate"}>
                        {f.filename}
                      </span>
                      <button
                        type={"button"}
                        aria-label={`Remove ${f.filename}`}
                        onClick={() =>
                          setFiles((p) => p.filter((x) => x.id !== f.id))
                        }
                        className={
                          "inline-flex text-[#B4B9C6] hover:text-[#3A4256]"
                        }>
                        <HiXMark size={13} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {sendError && (
              <div
                role={"alert"}
                className={
                  "mx-[22px] mb-[10px] flex flex-none items-start gap-[10px] rounded-[10px] border border-[#FECACA] bg-[#FEF2F2] px-[14px] py-[10px] text-[13px] leading-[1.45] text-[#991B1B]"
                }>
                <span className={"flex-1"}>
                  {sendError.text}
                  {sendError.kind === "mailbox" && (
                    <>
                      {" "}
                      <a
                        href={"/mail"}
                        target={"_blank"}
                        rel={"noreferrer"}
                        className={"font-bold text-[#6B4EFF] underline"}>
                        Open the Email page
                      </a>{" "}
                      (opens in a new tab, so this email stays here).
                    </>
                  )}
                </span>
                <button
                  type={"button"}
                  aria-label={"Close message"}
                  onClick={() => setSendError(null)}
                  className={"inline-flex text-[#B91C1C] hover:text-[#7F1D1D]"}>
                  <HiXMark size={14} />
                </button>
              </div>
            )}

            {checkOpen && checks.length > 0 && (
              <div
                className={
                  "mx-[22px] mb-[10px] flex-none rounded-[11px] border border-[#FCE3A3] bg-[#FFFBEB] px-[14px] py-3"
                }>
                <div className={"mb-[6px] flex items-center gap-2"}>
                  <span
                    className={
                      "flex-1 text-[13.5px] font-extrabold text-[#7A4A06]"
                    }>
                    Before you send
                  </span>
                  <button
                    type={"button"}
                    aria-label={"Close"}
                    onClick={() => setCheckOpen(false)}
                    className={"inline-flex text-[#C08A2E]"}>
                    <HiXMark size={14} />
                  </button>
                </div>
                {checks.map((c) => (
                  <div
                    key={c.kind}
                    className={"flex items-center gap-[10px] py-[5px]"}>
                    <span
                      className={
                        "h-[6px] w-[6px] flex-none rounded-full bg-[#D97706]"
                      }
                    />
                    <span className={"flex-1 text-[13px] text-[#5C3D0A]"}>
                      {c.text}
                    </span>
                    {c.kind === "attach" && (
                      <button
                        type={"button"}
                        onClick={() => fileRef.current?.click()}
                        className={
                          "h-[26px] flex-none rounded-[7px] border border-[#F3D28A] bg-white px-[10px] text-[12px] font-bold text-[#7A4A06]"
                        }>
                        Attach file
                      </button>
                    )}
                    {c.kind === "subject" && aiOn && (
                      <button
                        type={"button"}
                        onClick={suggestSubject}
                        className={
                          "h-[26px] flex-none rounded-[7px] border border-[#F3D28A] bg-white px-[10px] text-[12px] font-bold text-[#7A4A06]"
                        }>
                        Suggest one
                      </button>
                    )}
                    {c.kind === "unfinished" && aiOn && (
                      <button
                        type={"button"}
                        onClick={() => {
                          setCheckOpen(false);
                          runImprove("improve");
                        }}
                        className={
                          "h-[26px] flex-none rounded-[7px] border border-[#F3D28A] bg-white px-[10px] text-[12px] font-bold text-[#7A4A06]"
                        }>
                        Finish with AI
                      </button>
                    )}
                  </div>
                ))}
                <div className={"mt-[6px] flex justify-end"}>
                  <button
                    type={"button"}
                    onClick={() => send(true)}
                    className={
                      "text-[12.5px] font-bold text-[#7A4A06] underline"
                    }>
                    Send anyway
                  </button>
                </div>
              </div>
            )}

            {/* footer */}
            <div
              className={
                "flex flex-none items-center gap-2 border-t border-[#ECEFF3] bg-white px-[22px] py-3"
              }>
              <button
                type={"button"}
                onClick={() => fileRef.current?.click()}
                className={
                  "inline-flex h-[38px] items-center gap-[7px] rounded-[8px] border-[1.5px] border-[#DCE2EA] bg-white px-[13px] text-[13px] font-bold text-[#3A4256] hover:border-[#6B4EFF] hover:text-[#6B4EFF]"
                }>
                <HiOutlinePaperClip size={15} />
                Attach
              </button>
              <input
                ref={fileRef}
                type={"file"}
                multiple
                className={"hidden"}
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <button
                type={"button"}
                title={"Discard"}
                aria-label={"Discard"}
                onClick={onClose}
                className={
                  "inline-flex h-[38px] w-[38px] items-center justify-center rounded-[8px] text-[#8A90A2] hover:bg-[#FEECEC] hover:text-[#E5484D]"
                }>
                <HiOutlineTrash size={17} />
              </button>
              <span className={"flex-1"} />
              <button
                type={"button"}
                onClick={() => checks.length && setCheckOpen((v) => !v)}
                className={classNames(
                  "inline-flex h-[30px] items-center gap-[6px] whitespace-nowrap rounded-[15px] px-[11px] text-[12.5px] font-bold",
                  checks.length ? "cursor-pointer" : "cursor-default",
                )}
                style={
                  bodyEmpty
                    ? { background: "#F3F4F7", color: "#9097A8" }
                    : checks.length
                    ? { background: "#FEF3C7", color: "#92400E" }
                    : { background: "#E7F7EE", color: "#15803D" }
                }>
                {checks.length ? (
                  <span className={"h-[6px] w-[6px] rounded-full bg-current"} />
                ) : (
                  <LuCheck size={13} />
                )}
                {checkLabel}
              </button>
              <button
                type={"button"}
                onClick={onClose}
                className={
                  "h-10 rounded-[8px] border-[1.5px] border-[#DCE2EA] bg-white px-[18px] text-[14px] font-bold text-[#3A4256]"
                }>
                Cancel
              </button>
              <button
                type={"button"}
                onClick={() => send(false)}
                disabled={!canSend}
                title={hasMailbox ? undefined : NO_MAILBOX_TIP}
                className={
                  "inline-flex h-10 items-center gap-2 rounded-[8px] px-[22px] text-[14px] font-bold text-white"
                }
                style={{
                  background: canSend ? "#6B4EFF" : "#B7ABF7",
                  cursor: canSend ? "pointer" : "not-allowed",
                }}>
                {sending || replying ? (
                  <span
                    className={
                      "inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                    }
                  />
                ) : (
                  <LuSend size={15} />
                )}
                Send
              </button>
            </div>
          </div>

          {/* RIGHT: AI assistant */}
          {panelVisible && (
            <ComposeAiPanel
              isAnswer={isAnswer}
              replyingTo={
                isAnswer
                  ? email?.from?.[0]?.name || email?.from?.[0]?.email
                  : undefined
              }
              aiContext={aiContext}
              prompt={prompt}
              setPrompt={setPrompt}
              tone={tone}
              setTone={setTone}
              busy={!!busy}
              bodyEmpty={bodyEmpty}
              onGenerate={(text) => runDraft(text)}
              onReadyReply={applyReadyReply}
              onImprove={(action) => runImprove(action)}
            />
          )}
        </div>
      </div>

      {!!fileTooBig && (
        <SettingsConfirmModal
          noCancel
          confirmText={"Ok"}
          onClose={() => setFileTooBig(null)}
          onConfirm={() => setFileTooBig(null)}
          variant={"warning"}
          icon={<WarningExclaimSvg sizeCx={"h-6 w-6"} />}
          title={"Attachments too large"}
          description={fileTooBig}
        />
      )}
    </div>
  );
};
